import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Table, TablePaginationConfig } from "antd";
import { useGetCustomerQuery } from "../../../hooks/CustomerManagement/useGetCustomerQuery";
import SearchBox from "./components/SearchBox";
import Moment from "react-moment";
import { useDebounce } from "hooks/useDebounce";
import { Avatar } from "Pages/User/OrderManagement/Components/Avatar";

const CustomerManagement = () => {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial values from URL params
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  const pageSizeFromUrl = parseInt(searchParams.get("pageSize") || "15", 10);

  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [pageSize, setPageSize] = useState(pageSizeFromUrl);

  const navigate = useNavigate();

  const { data, isPending: isLoading } = useGetCustomerQuery({
    search: debouncedSearch,
    page: currentPage,
    limit: pageSize,
  });

  // Update URL when page or pageSize changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", currentPage.toString());
    newSearchParams.set("pageSize", pageSize.toString());

    if (debouncedSearch) {
      newSearchParams.set("search", debouncedSearch);
    } else {
      newSearchParams.delete("search");
    }

    setSearchParams(newSearchParams, { replace: true });
  }, [currentPage, pageSize, debouncedSearch, setSearchParams, searchParams]);

  // Sync search from URL on component mount
  useEffect(() => {
    const searchFromUrl = searchParams.get("search") || "";
    setSearch(searchFromUrl);
  }, [searchParams]);

  const handleTableChange = (newPagination: TablePaginationConfig) => {
    const newPage = newPagination.current ?? 1;
    const newPageSize = newPagination.pageSize ?? 15;

    setCurrentPage(newPage);
    setPageSize(newPageSize);
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const paginationConfig: TablePaginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: data?.data?.meta?.totalDocs || 0,
    showSizeChanger: true,
    showTotal: (total, range) =>
      `${range[0]}-${range[1]} of ${total} customers`,
    pageSizeOptions: ["15", "20", "50", "100"],
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (_: any, record: any) => (
        <span className="flex gap-3 items-center">
          {record.profileImage === "" ? (
            <Avatar fallback={record.firstName + " " + record.lastName} />
          ) : (
            <img
              src={record.profileImage}
              alt=""
              className="rounded-full w-10 h-10 bg-gray"
            />
          )}
          <span>
            {record.firstName} {record.lastName}
          </span>
        </span>
      ),
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "isSuspended",
      key: "isSuspended",
      render: (isSuspended: string) => (
        <span
          className={`p-3 rounded-full ${
            !isSuspended
              ? "text-[#0CA364] bg-[#E4FFEB]"
              : "text-[#F44336] bg-[#FFF4F3]"
          }`}
        >
          {isSuspended ? "Inactive" : "Active"}
        </span>
      ),
    },
    {
      title: "Orders",
      dataIndex: "orders",
      key: "orders",
    },
    {
      title: "Date Joined",
      dataIndex: "dateJoined",
      key: "createdAt",
      render: (createdAt: any) => (
        <Moment
          interval={0}
          format="DD-MM-YYYY hh:mm a"
          className="text-[#939393] italic"
        >
          {createdAt}
        </Moment>
      ),
    },
  ];

  return (
    <DashboardLayout
      title="Customer management"
      back={false}
      right={<SearchBox search={search} setSearch={setSearch} />}
    >
      <Table
        loading={isLoading}
        dataSource={data?.data?.docs}
        columns={columns}
        pagination={paginationConfig}
        onChange={handleTableChange}
        className="pl-[19px] pr-[40px] manrope mt-4"
        onRow={(record: any) => ({
          onClick: () => navigate(`/customer-management/${record.id}`),
        })}
      />
    </DashboardLayout>
  );
};

export default CustomerManagement;
